import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ADMIN_API_SERVER_URL } from '../tokens/admin-api-server-url.token';
import { ServerBaseService } from '../server-base.service';
import { CompanyBase, QuickBooksCompany, QuickBooksCompanyResponse, QuickBooksDataResponse, QuickBooksExportCompany, QuickBooksExportDetails, QuickBooksErrorCompany, QuickBooksExportRow } from '@equipmyschool/emsadminweb-models';
import { saveFile } from 'app/shared/save-file';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class QuickbooksService extends ServerBaseService {
  private refreshToken: string;
  private _refreshToken$ = new BehaviorSubject<string>(null);
  public refreshToken$ = this._refreshToken$.asObservable();

  private _qbExportRows$ = new BehaviorSubject<QuickBooksExportRow[]>(null);
  public qbExportRows$ = this._qbExportRows$.asObservable();

  private _qbExportRow$ = new BehaviorSubject<QuickBooksExportRow[]>(null);
  public qbExportRow$ = this._qbExportRow$.asObservable();

  private _qbExportDetails$ = new BehaviorSubject<QuickBooksExportDetails>(null);
  public qbExportDetails$ = this._qbExportDetails$.asObservable();

  private _qbExportErrors$ = new BehaviorSubject<QuickBooksErrorCompany[]>(null);
  public qbExportErrors$ = this._qbExportErrors$.asObservable();

  private _company$ = new BehaviorSubject<QuickBooksCompany>(null);
  public company$ = this._company$.asObservable();

  private _validAccounts$ = new BehaviorSubject<QuickBooksExportCompany[]>(null);
  public validAccounts$ = this._validAccounts$.asObservable();

  private _vendorList$ = new BehaviorSubject<QuickBooksExportCompany[]>(null);
  public vendorList$ = this._vendorList$.asObservable();

  private _companies$ = new BehaviorSubject<CompanyBase[]>(null);
  public companies$ = this._companies$.asObservable();
  
  constructor(http: HttpClient, @Inject(ADMIN_API_SERVER_URL) serverUrl: string) {
    super(http, serverUrl);
  }

  public getAuthToken (code: string) {
    this.request<string>('GET', 'QuickBooks/AuthToken', {code: code})
      .subscribe((response) => {
        this.refreshToken = response;
        this._refreshToken$.next(response);
      });
  }

  public getCompany (realmId: string) {
    this.request<QuickBooksCompanyResponse>('GET', 'QuickBooks/Company', {realmId: realmId, refreshToken: this.refreshToken})
      .subscribe((response) => {
         this._company$.next(response.Company);
         this.refreshToken = response.Token;
         this._refreshToken$.next(response.Token);
      });
  }

  public getData (type: string, realmId: string) {
    this.request<QuickBooksDataResponse>('GET', 'QuickBooks/' + type, { realmId: realmId, refreshToken: this.refreshToken })
      .subscribe((response) => {
         this._qbExportRows$.next(response.Data);
         this.refreshToken = response.Token;
         this._refreshToken$.next(response.Token);
      });
  }

  public getDataForSingle (type: string, realmId: string, Id: number) {
    this.request<QuickBooksDataResponse>('GET', 'QuickBooks/' + type + 'Company', { realmId: realmId, refreshToken: this.refreshToken, companyId: Id })
      .subscribe((response) => {
         this._qbExportRow$.next(response.Data);
         this.refreshToken = response.Token;
         this._refreshToken$.next(response.Token);
      });
  }

  public clearData() {
    this.refreshToken = null;
    this._refreshToken$.next(null);
    this._qbExportRows$.next(null);
    this._qbExportDetails$.next(null);
    this._qbExportErrors$.next(null);
    this._company$.next(null);
    this._validAccounts$.next(null);
    this._vendorList$.next(null);
  }

  public getDetailedData (type: string, realmId: string, company: any) {
    this.request<QuickBooksExportDetails>('GET', 'QuickBooks/' + type, { realmId: realmId, refreshToken: this.refreshToken, companyId: company})
      .subscribe((response) => {
        this._qbExportDetails$.next(response);
      });
  }
  public getDocumentsMissingVAT (type: string, realmId: string, company: number) {
    this.request<QuickBooksExportDetails>('GET', 'QuickBooks/' + type + 'MissingVAT', { realmId: realmId, refreshToken: this.refreshToken, companyId: company})
      .subscribe((response) => {
        this._qbExportDetails$.next(response);
      })
  }  
  public clearDetailedData() {
    this._qbExportDetails$.next(null);
  }

  public exportData (type: string, realmId: string, accounts: QuickBooksExportCompany[]) {
    this.request<QuickBooksDataResponse>('POST', 'QuickBooks/' + type, null, { realmId: realmId, refreshToken: this.refreshToken, accounts: accounts })
      .subscribe((response) => {
         // Clear out any previous errors
         this._qbExportErrors$.next(null);
         this._qbExportRows$.next(response.Data)
         this.refreshToken = response.Token;
         this._refreshToken$.next(response.Token);
      },
      (error: HttpErrorResponse) => {
        // If the error object contains a "NewData" property,
        // we have error details that can be shown to the user
        if ('NewData' in error.error) {
          this._qbExportErrors$.next(error.error.Errors);
          this._qbExportRows$.next(error.error.NewData.Data);
          this.refreshToken = error.error.NewData.Token;
          this._refreshToken$.next(error.error.NewData.Token);
        }
      });
  }

  public reconcileData (realmId: string) {
    this.requestFile('GET', 'QuickBooks/Reconcile', {realmId: realmId, refreshToken: this.refreshToken}).subscribe(
      (result) => {
        if (result && result.body.size > 0) {
          saveFile(result);
        } else {
          alert("Nothing to reconcile");
        }
      }
    );
  }

  public getValidAccounts(companyType: number, realmId: string, accounts: string[]) {
    this.request<QuickBooksExportCompany[]>('GET', 'QuickBooks/ValidAccounts', {RealmId: realmId, RefreshToken: this.refreshToken, CompanyType: companyType, Accounts: accounts})
      .subscribe((response) => { this._validAccounts$.next(response) });
  }

  public async searchSuppliers (type: String, searchText: string, realmId: string) {
    if (!searchText) {
      this._vendorList$.next(null);
    }
    else {
      this.request<QuickBooksExportCompany[]>('GET', 'QuickBooks/SearchQuickBooksVendors', {realmId: realmId, refreshToken: this.refreshToken, type: type, searchText: searchText})
        .subscribe((response) => {
          this._vendorList$.next(response);
        })
    }
  }

  public async searchClients (type: String, searchText: string, realmId: string) {
    if (!searchText) {
      this._vendorList$.next(null);
    }
    else {
      this.request<QuickBooksExportCompany[]>('GET', 'QuickBooks/SearchQuickBooksClients', {realmId: realmId, refreshToken: this.refreshToken, type: type, searchText: searchText})
        .subscribe((response) => {
          this._vendorList$.next(response);
        })
    }
  }

  public updateSupplier (supplier: QuickBooksExportCompany, realmId: string) {
    return this.request('POST', 'QuickBooks/UpdateSupplier', null, { realmId: realmId, refreshToken: this.refreshToken, company: supplier });
  }

  public getCompanies() {
    this.request<CompanyBase[]>('GET', 'EmsCompany/Get')
      .subscribe((response) => {
        this._companies$.next(response);
      });
  }
  
  public compareCompanies(a: CompanyBase, b: CompanyBase) {
    return a && b ? a.CompanyID === b.CompanyID : a === b;
  }
}


